import React, { useState, useEffect } from 'react';
import { FoxSpinnerInline } from '../ui/FoxSpinner';
import { packagesAPI } from '../../services/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { Plus, Edit, Trash2, Package } from 'lucide-react';
import { toast } from 'sonner';

const PackagesManagement = () => {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPackage, setEditingPackage] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    coins: '',
    price: '',
    description: '',
    active: true
  });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    loadPackages();
  }, []);

  const loadPackages = async () => {
    try {
      const response = await packagesAPI.list(false);
      setPackages(response.data);
    } catch (error) {
      toast.error('Erro ao carregar pacotes');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (pkg = null) => {
    if (pkg) {
      setEditingPackage(pkg);
      setFormData({
        name: pkg.name,
        coins: pkg.coins.toString(),
        price: pkg.price.toString(),
        description: pkg.description || '',
        active: pkg.active
      });
    } else {
      setEditingPackage(null);
      setFormData({ name: '', coins: '', price: '', description: '', active: true });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const data = {
      name: formData.name,
      coins: parseInt(formData.coins),
      price: parseFloat(formData.price),
      description: formData.description || null,
      active: formData.active
    };

    try {
      if (editingPackage) {
        await packagesAPI.update(editingPackage.id, data);
        toast.success('Pacote atualizado com sucesso!');
      } else {
        await packagesAPI.create(data);
        toast.success('Pacote criado com sucesso!');
      }
      setDialogOpen(false);
      loadPackages();
    } catch (error) {
      console.error('Erro ao salvar pacote:', error);
      console.error('Detalhes:', error.response?.data);
      
      // Extrai mensagem de erro corretamente
      let errorMsg = 'Erro ao salvar pacote';
      if (error.response?.data) {
        const data = error.response.data;
        if (typeof data.detail === 'string') {
          errorMsg = data.detail;
        } else if (Array.isArray(data.detail)) {
          errorMsg = data.detail.map(e => e.msg || JSON.stringify(e)).join(', ');
        } else if (data.message) {
          errorMsg = data.message;
        }
      }
      toast.error(errorMsg);
    }
  };

  const handleDelete = (id) => {
    setDeletingId(id);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    try {
      await packagesAPI.delete(deletingId);
      toast.success('Pacote excluído com sucesso!');
      setShowDeleteConfirm(false);
      setDeletingId(null);
      loadPackages();
    } catch (error) {
      toast.error('Erro ao excluir pacote');
      setShowDeleteConfirm(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center p-8"><FoxSpinnerInline size="lg" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="packages-management">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Gerenciar Pacotes</h2>
          <p className="text-gray-600">Adicione, edite ou remova pacotes de moedas</p>
        </div>
        <Button data-testid="add-package-button" onClick={() => handleOpenDialog()} className="bg-purple-600 hover:bg-purple-700">
          <Plus className="mr-2 h-4 w-4" />
          Novo Pacote
        </Button>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {packages.map((pkg) => (
          <Card key={pkg.id} data-testid={`package-card-${pkg.id}`} className={!pkg.active ? 'opacity-60' : ''}>
            <CardHeader>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2">
                  <Package className="h-5 w-5 text-purple-600" />
                  <CardTitle className="text-lg">{pkg.name}</CardTitle>
                </div>
                <Badge variant={pkg.active ? 'default' : 'secondary'}>
                  {pkg.active ? 'Ativo' : 'Inativo'}
                </Badge>
              </div>
              <CardDescription>{pkg.description || 'Sem descrição'}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-2xl font-bold text-purple-600">{pkg.coins.toLocaleString('pt-BR')} moedas</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-1">
                    <span className="text-gray-500">PIX:</span>
                    <span className="font-bold text-orange-600">R$ {pkg.price.toFixed(2)}</span>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button data-testid={`edit-package-${pkg.id}`} onClick={() => handleOpenDialog(pkg)} variant="outline" className="flex-1">
                    <Edit className="mr-2 h-4 w-4" />
                    Editar
                  </Button>
                  <Button data-testid={`delete-package-${pkg.id}`} onClick={() => handleDelete(pkg.id)} variant="destructive" className="flex-1">
                    <Trash2 className="mr-2 h-4 w-4" />
                    Excluir
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Dialog de Criar/Editar */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="package-dialog">
          <DialogHeader>
            <DialogTitle>{editingPackage ? 'Editar Pacote' : 'Novo Pacote'}</DialogTitle>
            <DialogDescription>
              {editingPackage ? 'Altere as informações do pacote' : 'Preencha os dados do novo pacote'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Nome do Pacote</Label>
              <Input
                data-testid="package-name-input"
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="coins">Quantidade de Moedas</Label>
                <Input
                  data-testid="package-coins-input"
                  id="coins"
                  type="number"
                  min="1"
                  value={formData.coins}
                  onChange={(e) => setFormData({ ...formData, coins: e.target.value })}
                  required
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="price">Preço PIX (R$)</Label>
                <Input
                  data-testid="package-price-input"
                  id="price"
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  required
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Descrição (opcional)</Label>
              <Textarea
                data-testid="package-description-input"
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
              />
            </div>
            <div className="flex items-center space-x-2">
              <Switch
                data-testid="package-active-switch"
                id="active"
                checked={formData.active}
                onCheckedChange={(checked) => setFormData({ ...formData, active: checked })}
              />
              <Label htmlFor="active">Pacote ativo</Label>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancelar
              </Button>
              <Button data-testid="save-package-button" type="submit" className="bg-purple-600 hover:bg-purple-700">
                {editingPackage ? 'Salvar Alterações' : 'Criar Pacote'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog de Confirmação de Delete */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmar Exclusão</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-600">
            Tem certeza que deseja excluir este pacote?
          </p>
          <p className="text-xs text-red-600 mt-2">
            ⚠️ Esta ação não pode ser desfeita.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteConfirm(false)}>
              Cancelar
            </Button>
            <Button variant="destructive" onClick={confirmDelete}>
              <Trash2 className="mr-2 h-4 w-4" />
              Confirmar Exclusão
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PackagesManagement;